import { useEffect, useRef, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {
  IconExpand,
  ButtonSecondary,
  ButtonSize,
  useQueryState,
  IconDownload,
  useScroll,
  useFilters,
  Table,
  IconLoader,
  Popup,
  useGetList,
  SchemaFilterType,
  PermissionDenied,
  usePrevious,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  agreementsListColumns,
  agreementsListSchema,
  agreementsListColumnsLabels,
  agreementsListFiltersSchema,
} from './variables/agreement';
import {
  useLazyGetAgreementsQuery,
  useChangeAgreementsStatusMutation,
  useLazyGetEmployeesAgreementFileQuery,
} from './store/services/agreements';
import { Agreement, AgreementStatus } from './types';
import formatAgreementsList, {
  getAgreementTypeName,
} from './helpers/format-agreements-list';
import {
  onSelectedRows,
  setTakeCount as setStoreTakeCount,
} from './store/slices/agreements';
import { PermissionEnum } from '../user-roles/types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const AgreementsList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    settings: {
      agreements: {
        list: { selectedRows, values, pagination, errorsList },
      },
    },
    administrationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [takeCount, setTakeCount] = useState(0);
  const [downloadFile, setDownloadedFile] = useState('');
  const [archivePopupVisibile, setArchivePopupVisible] = useState(false);

  const dispatch = useAppDispatch();

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(agreementsListSchema);

  const prevFilters = usePrevious(filters);

  const [getAgreements, { isLoading, isFetching }] =
    useLazyGetAgreementsQuery();

  const [
    changeAgreementStatus,
    { isLoading: statusChangeInProgress, isSuccess },
  ] = useChangeAgreementsStatusMutation();

  const [
    getFile,
    { isLoading: fileDownloadInProgress, isFetching: fileDownloadIsFetching },
  ] = useLazyGetEmployeesAgreementFileQuery();

  const { getListAction, onSort, onFilter, setPage, errorList } =
    useGetList<Agreement>({
      getList: getAgreements,
      onFilterChange,
      onSortChange,
      schema: agreementsListSchema,
    });

  const { search } = useLocation();

  const getAgreementFile = async (id: string) => {
    setDownloadedFile(id);

    const response = await getFile(id);

    if (response.data) {
      const currentAgreement = values?.find((item) => item.id === id);

      if (currentAgreement) {
        const hiddenElement = document.createElement('a');
        const url = window.URL || window.webkitURL;

        // @ts-ignore
        const blob = url.createObjectURL(response.data);
        hiddenElement.href = blob;
        hiddenElement.target = '_blank';
        hiddenElement.download = `${getAgreementTypeName(
          currentAgreement?.type,
        )}(v${currentAgreement?.version})`;
        hiddenElement.click();

        setDownloadedFile('');
      }
    }
  };

  const getFiles = async () => {
    const selectedAgreements: Agreement[] = [];

    if (selectedRows && selectedRows.length) {
      const downloadedFiles = await Promise.all(
        selectedRows.map(async (item) => {
          selectedAgreements.push(
            values?.find((agreement) => agreement.id === item) as Agreement,
          );

          const response = await getFile(item);

          return response.data;
        }),
      );

      if (downloadedFiles) {
        downloadedFiles.forEach((file, index) => {
          const hiddenElement = document.createElement('a');
          const url = window.URL || window.webkitURL;

          // @ts-ignore
          const blob = url.createObjectURL(file);
          hiddenElement.href = blob;
          hiddenElement.target = '_blank';
          hiddenElement.download = `${getAgreementTypeName(
            selectedAgreements[index].type,
          )}(v${selectedAgreements[index].version})`;
          hiddenElement.click();
        });
      }
    }
  };

  const formattedData = useMemo(() => {
    const data = formatAgreementsList(
      values as Agreement[],
      getAgreementFile,
      downloadFile,
      fileDownloadInProgress || fileDownloadIsFetching,
    );

    return data;
  }, [
    values,
    sort,
    fileDownloadInProgress,
    fileDownloadIsFetching,
    downloadFile,
  ]);

  const onRowsSelect = (rows: string[]) => {
    if (!isEqual(selectedRows, rows)) {
      dispatch(onSelectedRows(rows));
    }
  };

  const onStatusChange = () => {
    const publishedIds: string[] = [];

    selectedRows?.forEach((id) => {
      const currentAgreement = values?.find((item) => item.id === id);

      if (
        currentAgreement &&
        currentAgreement.status !== AgreementStatus.ARCHIVED
      ) {
        publishedIds.push(currentAgreement.id);
      }
    });

    changeAgreementStatus(publishedIds);
  };

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: agreementsListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    let title = 'Settings: Agreements';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  useEffect(() => {
    if (isSuccess) {
      setArchivePopupVisible(false);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (
      takeCount !== 0 &&
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !values &&
      sort &&
      Object.keys(sort).length &&
      activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST)
    ) {
      getListAction({
        filters: {
          ...filters,
          take: {
            value: [takeCount.toString()],
            type: SchemaFilterType.STRING,
          },
        },
        sort,
      });
    }
  }, [filters, sort, values, takeCount, activePermissions]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 47;

        setTakeCount(Math.ceil(take + 3));

        dispatch(setStoreTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  let content: React.JSX.Element;

  if (isLoading) {
    content = (
      <div className={cn.agreementsLoader}>
        <IconLoader />
      </div>
    );
  } else if (!activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <Table
        columns={agreementsListColumns}
        data={formattedData || []}
        onSort={(name) => onSort(name)}
        filters={filters}
        sort={sort}
        columnsLabels={agreementsListColumnsLabels}
        onFilterChange={(filterValues) => onFilter(filterValues)}
        filtersComponent={filtersComponent}
        isLoading={isFetching || takeCount === 0 || isLoading}
        handleSubmit={handleSubmit}
        onRowsSelect={onRowsSelect}
        pagination={pagination}
        setPage={(data) => setPage({ ...data, filters })}
        take={takeCount.toString()}
        error={errorsList || errorList}
        disabledRowIds={values
          ?.filter((item) => item.status === AgreementStatus.ARCHIVED)
          .map((item) => item.id)}
        clickable
      />
    );
  }

  return (
    <div className={cn.agreementsWrapper}>
      <div className={cn.settingsTitle}>Agreements</div>

      <div className={cn.agreements}>
        <div className={cn.agreementsTop}>
          {activePermissions?.includes(PermissionEnum.AGREEMENTS_LIST) &&
            activePermissions.includes(
              PermissionEnum.AGREEMENTS_CREATE_ACTION,
            ) && (
              <NavLink
                to={`${ROUTES.AGREEMENTS_ADD}${search || ''}`}
                className={cn.addAgreement}
              >
                <div className={cn.addAgreementContent}>
                  <IconExpand />
                  <span>Add Agreement</span>
                </div>
              </NavLink>
            )}

          {selectedRows?.length > 0 && (
            <>
              <div className={cn.separator} />
              <ButtonSecondary
                className={`button-white ${cn.controlButton}`}
                text={statusChangeInProgress ? 'Loading...' : 'Archive'}
                size={ButtonSize.SMALL}
                onClick={() => setArchivePopupVisible(true)}
                disabled={statusChangeInProgress}
              />
              <ButtonSecondary
                className={`button-white ${cn.controlButton} ${cn.downloadButton}`}
                icon={<IconDownload />}
                text={
                  fileDownloadInProgress || fileDownloadIsFetching
                    ? 'Loading...'
                    : 'Download'
                }
                size={ButtonSize.SMALL}
                onClick={() => getFiles()}
                disabled={fileDownloadInProgress || fileDownloadIsFetching}
              />
            </>
          )}
        </div>
        <div className={cn.agreementsBottom} ref={scrollRef}>
          {content}
        </div>
        <Popup
          title={`Archivate ${selectedRows.length} agreement${
            selectedRows.length > 1 ? 's' : ''
          }`}
          onCancelClick={() => setArchivePopupVisible(false)}
          onOkClick={() => onStatusChange()}
          cancelButtonText="Cancel"
          okButtonText="Yes, archivate"
          isVisible={archivePopupVisibile}
          isLoading={statusChangeInProgress}
        />
        <Outlet />
      </div>
    </div>
  );
};

export default AgreementsList;
